import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { DataGrid, gridClasses, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { RootState } from '../../../../reducers';
import { getActiveProviders } from '../../../../store/provider.slice';
import {
    exportRequestsReport,
    fetchRequestsReport,
    fetchRequestTypeChoices,
    setEndDate,
    setPriority,
    setProviderId,
    setRequestProviderType,
    setStartDate,
    setStatus,
    setRowData,
} from '../../../../store/requestReport.slice';
import { localDateFormat } from '../../../../utils/timeFormat';
import { copyObject } from '../../../../utils/common';
import Loading from '../../../Shared/Loading';
import RequestTypeAutocomplete from './components/Autocompletes/RequestTypeAutocomplete';
import { fetchTeams } from '../../../../store/careTeams.slice';
import { isDefaultDate } from '../../../../utils/common';

const RequestsReport = () => {
    const dispatch = useAppDispatch();
    const { startDate, endDate, providerId, priority, rowData, status, requestType, requestProviderType } = useSelector(
        (state: RootState) => state.requestReport
    );
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const { downLoading } = useSelector((state: RootState) => state.shared);
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const [showInActive, setShowInActive] = useState<boolean>(false);
    const [team, setTeam] = useState('All');
    const [showInActiveTeams, setShowInActiveTeams] = useState(false);
    const copyProviders = copyObject(activeProviders);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(11);
    const filteredProviders = (showInActive ? copyProviders : copyProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const filteredTeams = showInActiveTeams ? teams : teams.filter(t => !t.IsDeleted);

    useEffect(() => {
        dispatch(getActiveProviders(true));
        dispatch(setProviderId('All'));
        dispatch(fetchRequestTypeChoices());
        dispatch(fetchTeams(true));
        setTeam('NotSelected');
    }, [dispatch]);

    const priorityFormatter = (value: number) => {
        if (value === 1) {
            return 'Low';
        }
        if (value === 2) {
            return 'Medium';
        }
        if (value === 3) {
            return 'High';
        }
    };

    const statusFormatter = (value: number) => {
        if (value === 1) {
            return 'New';
        }
        if (value === 2) {
            return 'In-Progress';
        }
        if (value === 3) {
            return 'Completed';
        }
        if (value === 4) {
            return 'Archived';
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'AhcccsId',
            headerName: 'AHCCCS Id',
            minWidth: 110,
            flex: 1,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: 180,
            flex: 1,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 120,
            flex: 1,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? '-' : localDateFormat(new Date(row.DOB))}`,
        },
        {
            field: 'MemberStatus',
            headerName: 'Status',
            minWidth: 180,
            flex: 1,
        },
        {
            field: 'HealthPlan',
            headerName: 'Health Plan',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'Population',
            headerName: 'Population',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'PrimaryProvider',
            headerName: 'Primary Provider',
            width: 180,
        },
        {
            field: 'TeamName',
            headerName: 'Team',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'Type',
            headerName: 'Type',
            minWidth: 160,
            flex: 1,
            valueGetter: (value, row) => row.RequestTypeLabel,
        },
        {
            field: 'Priority',
            headerName: 'Priority',
            minWidth: 160,
            flex: 1,
            valueGetter: (value, row) => priorityFormatter(row.Priority),
        },
        {
            field: 'Status',
            headerName: 'Request Status',
            minWidth: 180,
            flex: 1,
            valueGetter: (value, row) => statusFormatter(row.Status),
        },
        {
            field: 'CreatedOn',
            headerName: 'Creation Date',
            minWidth: 110,
            flex: 1,
            valueGetter: (value, row) => localDateFormat(new Date(row.CreatedOn)),
        },
        {
            field: 'CompletedOn',
            headerName: 'Completion Date',
            minWidth: 110,
            flex: 1,
            valueGetter: (value, row) => (row.CompletedOn ? localDateFormat(new Date(row.CompletedOn)) : ''),
        },
        {
            field: 'RequestFrom',
            headerName: 'Initiated By',
            minWidth: 160,
            flex: 1,
            // Fixes bug where Initiated By value is left blank
            renderCell: (params: GridRenderCellParams) => {
                return params.row.RequestFrom === null ? (
                    <Typography color={'error'} variant="subtitle2">
                        No Value Entered
                    </Typography>
                ) : (
                    <Typography variant="body2">{params?.row?.RequestFrom?.Name ?? 'Missing Name'}</Typography>
                );
            },
        },
        {
            field: 'RequestTo',
            headerName: 'Assigned To',
            minWidth: 160,
            flex: 1,
            valueGetter: (value, row) => `${row?.RequestTo?.Name ?? 'Missing Name'}`,
        },
    ];

    const priorityChoices = [
        { label: 'Low', value: '1' },
        { label: 'Medium', value: '2' },
        { label: 'High', value: '3' },
    ];

    const priorityHandler = useCallback(
        (event: React.MouseEvent<HTMLElement>, newPriorites: string[]) => {
            if (newPriorites.length) {
                dispatch(setPriority(newPriorites));
            }
        },
        [dispatch]
    );

    const statusChoices = [
        { label: 'New', value: '1' },
        { label: 'In-Progress', value: '2' },
        { label: 'Completed', value: '3' },
    ];

    const statusHandler = useCallback(
        (event: React.MouseEvent<HTMLElement>, newStatuses: string[]) => {
            dispatch(setStatus(newStatuses));
        },
        [dispatch]
    );

    const handleProviderType = useCallback(
        (event: React.MouseEvent<HTMLElement>, providerType: string | null) => {
            dispatch(setRequestProviderType(providerType));
        },
        [dispatch]
    );

    const toggleInactiveHandler = useCallback(
        (isChecked: boolean) => {
            setShowInActive(isChecked);
            dispatch(setProviderId('All'));
            dispatch(setRowData([]));
            setTeam("NotSelected");
        },
        [dispatch]
    );

    const toggleInactiveTeamsHandler = (isChecked: boolean) => {
        setShowInActiveTeams(isChecked);
        setTeam('All');
        dispatch(setProviderId("NotSelected"));
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container>
                <Box width={'100%'} borderBottom={1} paddingBottom={1} marginBottom={1}>
                    <Typography fontWeight="fontWeightMedium" component="h3">
                        Requests Report
                    </Typography>
                </Box>
                {<Loading message={'Loading...'} />}
                <Grid size={12}>
                    <Stack width="100%" direction="row" alignItems="center" marginY={2} className="float-right" spacing={3}>
                        <FormControl className="form-control-200">
                            <DesktopDatePicker
                                label="Start Date:"
                                value={startDate}
                                minDate={new Date('2017-01-01')}
                                onChange={(newValue) => {
                                    dispatch(setStartDate(newValue));
                                }}
                                slotProps={{ textField: { size: 'small' } }}
                            />
                        </FormControl>
                        <FormControl className="form-control-200">
                            <DesktopDatePicker
                                label="End Date:"
                                value={endDate}
                                minDate={new Date('2017-01-01')}
                                onChange={(newValue) => {
                                    dispatch(setEndDate(newValue));
                                }}
                                slotProps={{ textField: { size: 'small' } }}
                            />
                        </FormControl>
                        <FormControl className="form-control-200">
                            <RequestTypeAutocomplete />
                        </FormControl>
                        <FormControl className="form-control-200">
                            <FormControl className="width-100p">
                                <InputLabel id="demo-simple-select-label">Team</InputLabel>
                                <Select
                                    value={team}
                                    size="small"
                                    label="Team"
                                    renderValue={(selected) => {
                                        if (selected === "NotSelected") {
                                            return <span style={{ color: "gray" }}>Not Selected</span>;
                                        }

                                        if (selected === "All") {
                                            return "All";
                                        }

                                        const team = filteredTeams.find(p => p.Id === selected);
                                        return team
                                            ? `${team.Name}`
                                            : "";
                                    }}
                                    onChange={(e) => {
                                        const value = e.target.value as string;
                                        setTeam(value);

                                        // reset Provider to NotSelected
                                        if (value !== "NotSelected") {
                                            dispatch(setProviderId("NotSelected"));
                                        }
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                                >
                                    <MenuItem value="NotSelected" disabled sx={{ color: 'gray' }}>
                                        Not Selected
                                    </MenuItem>
                                    <MenuItem key="All" value="All">
                                        All
                                    </MenuItem>
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={<Checkbox checked={showInActiveTeams} onChange={(e) => toggleInactiveTeamsHandler(e.target.checked)} />}
                                        label="Show Inactive"
                                    />
                                    {filteredTeams.map((team) => {
                                        return (
                                            <MenuItem
                                                key={team.Id}
                                                value={team.Id}
                                                sx={{ color: team.IsDeleted ? "gray" : "black" }}
                                            >
                                                {team.Name}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </FormControl>
                        <Button
                            style={{ minWidth: '120px', marginRight: 'auto' }}
                            onClick={() =>
                                dispatch(
                                    fetchRequestsReport(
                                        providerId,
                                        team,
                                        requestProviderType,
                                        startDate,
                                        endDate,
                                        requestType,
                                        priority,
                                        status,
                                        showInActive,
                                        showInActiveTeams
                                    )
                                )
                            }
                            variant="contained"
                        >
                            Search
                        </Button>
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() =>
                                dispatch(
                                    exportRequestsReport(
                                        providerId,
                                        team,
                                        requestProviderType,
                                        startDate,
                                        endDate,
                                        requestType,
                                        priority,
                                        status,
                                        showInActive,
                                        showInActiveTeams
                                    )
                                )
                            }
                            variant="contained"
                            color="success"
                        >
                            {downLoading ? 'Downloading..' : 'Export'}
                        </Button>
                    </Stack>
                    <Stack width="100%" direction="row" alignItems="flex-end" marginBottom={2} className="float-right" spacing={3}>
                        <FormControl>
                            <ToggleButtonGroup
                                sx={{
                                    [`& .MuiToggleButtonGroup-grouped.Mui-selected`]: {
                                        backgroundColor: '#1976d2',
                                        color: '#fff',
                                    },
                                    whiteSpace: 'nowrap',
                                }}
                                value={requestProviderType}
                                size="small"
                                exclusive
                                onChange={handleProviderType}
                                aria-label="request provider type"
                            >
                                <ToggleButton value={'1'}>Initiated By</ToggleButton>
                                <ToggleButton value={'2'}>Assigned To</ToggleButton>
                            </ToggleButtonGroup>
                        </FormControl>
                        <FormControl className="form-control-200">
                            <InputLabel id="demo-simple-select-label">Provider</InputLabel>
                            <Select
                                size="small"
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                defaultValue={providerId}
                                value={providerId}
                                label="Provider"
                                renderValue={(selected) => {
                                    if (selected === "NotSelected") {
                                        return <span style={{ color: "gray" }}>Not Selected</span>;
                                    }

                                    if (selected === "All") {
                                        return "All";
                                    }

                                    const provider = filteredProviders.find(p => p.Id === selected);
                                    return provider
                                        ? `${provider.LastName}, ${provider.FirstName}`
                                        : "";
                                }}
                                onChange={(e) => {
                                    const value = e.target.value as string;
                                    dispatch(setProviderId(e.target.value));

                                    // reset Team to NotSelected
                                    if (value !== "NotSelected") {
                                        setTeam("NotSelected");
                                    }
                                }}
                            >
                                <MenuItem value={'All'}>All</MenuItem>
                                <FormControlLabel
                                    sx={{ pl: '12px', mb: '0px' }}
                                    control={<Checkbox checked={showInActive} onChange={(e) => toggleInactiveHandler(e.target.checked)} />}
                                    label="Show Inactive"
                                />
                                {[...filteredProviders]
                                    .sort((a, b) => a.LastName.localeCompare(b.LastName))
                                    .map((provider) => (
                                        <MenuItem
                                            value={provider.Id}
                                            key={provider.Id}
                                            sx={{ color: provider.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}
                                        >
                                            {`${provider.LastName}, ${provider.FirstName}`}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <FormControl className="form-control-200">
                            <FormLabel component="legend">Priority</FormLabel>
                            <ToggleButtonGroup
                                sx={{
                                    [`& .MuiToggleButtonGroup-grouped.Mui-selected`]: {
                                        backgroundColor: '#1976d2',
                                        color: '#fff',
                                    },
                                }}
                                value={priority}
                                size="small"
                                onChange={priorityHandler}
                                aria-label="priorities"
                            >
                                {priorityChoices.map((item) => (
                                    <ToggleButton key={`priority-${item.value}`} value={item.value}>
                                        {item.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </FormControl>

                        <FormControl>
                            <FormLabel component="legend">Status</FormLabel>
                            <ToggleButtonGroup
                                sx={{
                                    [`& .MuiToggleButtonGroup-grouped.Mui-selected`]: {
                                        backgroundColor: '#1976d2',
                                        color: '#fff',
                                    },
                                    whiteSpace: 'nowrap',
                                }}
                                value={status}
                                size="small"
                                onChange={statusHandler}
                                aria-label="statuses"
                            >
                                {statusChoices.map((item) => (
                                    <ToggleButton key={`status-${item.value}`} value={item.value}>
                                        {item.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </FormControl>
                    </Stack>
                </Grid>
                <Grid style={{ height: '72vh', width: '100%' }}>
                    <DataGrid
                        rows={rowData}
                        columns={columns}
                        paginationModel={{ pageSize: pageSize, page: page }}
                        onPaginationModelChange={(model) => {
                            setPageSize(model.pageSize);
                            setPage(model.page);
                        }}
                        pageSizeOptions={[5]}
                        getRowId={(row) => row.Id}
                        getRowHeight={() => 'auto'}
                        sx={{
                            [`& .${gridClasses.cell}`]: {
                                py: 1,
                            },
                        }}
                    />
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
};

export default RequestsReport;
